import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Copy, SettingsIcon, TrashIcon, X } from 'lucide-react';

import { closeModal, openModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { env } from '@/constants/env';
import { queryClient } from '@/lib/react-query';

import {
  type AdditionalData,
  getDigitalTwinsOptions,
  type PresignedUrl,
  useShareDigiTwin,
  useUpdateDigiTwinPresignedUrl,
} from '../../api/get-digital-twins';
import { useRevokePresignedUrl } from '../../api/presigned-urls';
import { ShareDigiTwinPopup } from './share-digi-twin-popup';

dayjs.extend(localizedFormat);

type SettingPopupState =
  | {
      type: 'NEW';
    }
  | {
      type: 'EDIT';
      token: string;
    };

export interface ShareDigiTwinModalProps {
  estateId: string;
  id: string;
  planId: string;
  presignedUrls: PresignedUrl[];
}

export const ShareDigiTwinModal: React.FC<ShareDigiTwinModalProps> = ({
  estateId,
  id,
  planId,
  presignedUrls,
}) => {
  const [settingPopup, setSettingPopup] = useState<SettingPopupState>();
  // necessary to correctly remove a deleted link without closing the modal
  const [displayedLinks, setDisplayedLinks] = useState(presignedUrls);
  const { t } = useTranslation();

  const openShareLinkSettingPopup = (token?: string) => {
    if (token) {
      setSettingPopup({ type: 'EDIT', token });
    } else {
      setSettingPopup({ type: 'NEW' });
    }
  };

  const closeShareLinkSettingPopup = () => {
    setSettingPopup(undefined);
  };

  const { mutate: shareDigiTwin } = useShareDigiTwin({
    mutationConfig: {
      onSuccess: ([{ token, expiryDate, additionalData, createdAt }]) => {
        const url = `${env.PUBLIC_EDITOR_URL}/view?token=${token}`;
        navigator.clipboard.writeText(url);
        toast.success(t('copied-url'));
        setDisplayedLinks((prev) => [...prev, { token, expiryDate, additionalData, createdAt }]);
        closeShareLinkSettingPopup();
        queryClient.invalidateQueries({ queryKey: getDigitalTwinsOptions(estateId).queryKey });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutate: updateDigiTwinPresignedUrl } = useUpdateDigiTwinPresignedUrl({
    mutationConfig: {
      onSuccess: ([{ token, expiryDate, additionalData, createdAt }]) => {
        toast.success(t('sharedLinkUpdated'));
        setDisplayedLinks((prev) => {
          const index = prev.findIndex((el) => el.token === token);
          const updatedLinks = [...prev];
          updatedLinks.splice(index, 1, { token, expiryDate, additionalData, createdAt });
          return updatedLinks;
        });
        closeShareLinkSettingPopup();
        queryClient.invalidateQueries({ queryKey: getDigitalTwinsOptions(estateId).queryKey });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutateAsync: revokeToken } = useRevokePresignedUrl({
    mutationConfig: {
      onSuccess: ([{ token }]) => {
        toast.success(t('successfully-revoked-link'));
        setDisplayedLinks(displayedLinks.filter((l) => l.token !== token));
        queryClient.invalidateQueries({ queryKey: getDigitalTwinsOptions(estateId).queryKey });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const handleRevoke = (token: string) => {
    openModal('confirm-delete-modal', {
      title: t('deleteSharedLink'),
      description: t('confirmDeleteSharedLink'),
      onSubmit: async () => {
        try {
          await revokeToken({ token });
        } finally {
          // re-open share-digi-twin modal
          openModal('share-digi-twin', {
            estateId,
            id,
            planId,
            presignedUrls,
          });
        }
      },
      onClose: () => {
        // re-open share-digi-twin modal
        openModal('share-digi-twin', {
          estateId,
          id,
          planId,
          presignedUrls,
        });
      },
    });
  };

  const createNewPresignedUrl = ({
    expiresIn,
    ...additionalData
  }: AdditionalData & { expiresIn: string }) => {
    if (!id) return;
    shareDigiTwin({
      id,
      planId,
      expiresIn: Number(expiresIn),
      additionalData,
    });
  };

  const updatePresignedUrl = (
    token: string,
    { expiresIn, ...additionalData }: AdditionalData & { expiresIn: string }
  ) => {
    if (!id) return;
    updateDigiTwinPresignedUrl({
      id,
      token,
      expiresIn: Number(expiresIn),
      additionalData,
    });
  };

  return (
    <ModalBase className="w-[40vw]">
      <X className="ml-auto cursor-pointer stroke-[4px] text-primary-light" onClick={closeModal} />
      <section className="mt-4 flex flex-row justify-between">
        <h3 className="text-xl font-semibold">Links</h3>
        <ShareDigiTwinPopup
          open={settingPopup?.type === 'NEW'}
          onOpen={() => openShareLinkSettingPopup()}
          onShare={createNewPresignedUrl}
          onCancel={closeShareLinkSettingPopup}
        >
          <Button>{t('createNewLink')}</Button>
        </ShareDigiTwinPopup>
      </section>
      {displayedLinks.length > 0 ? (
        <Table className="my-5 block max-h-[50vh] w-full table-auto bg-transparent">
          <TableHeader className="h-11 max-sm:hidden">
            <TableRow className="w-full justify-between">
              <TableHead className="text-center text-text-secondary">Link</TableHead>
              <TableHead className="text-center text-text-secondary">{t('created-at')}</TableHead>
              <TableHead className="text-center text-text-secondary">{t('expires-at')}</TableHead>
              <TableHead className="text-center text-text-secondary">{t('type')}</TableHead>
              <TableHead className="text-center text-text-secondary"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedLinks.map((presignedUrl) => {
              const url = `${env.PUBLIC_EDITOR_URL}/view?token=${presignedUrl.token}`;
              const expiresIn = dayjs(presignedUrl.expiryDate).diff(
                dayjs(presignedUrl.createdAt),
                'days'
              );
              if (dayjs().isAfter(presignedUrl.expiryDate)) return;
              return (
                <TableRow key={presignedUrl.token} className="items-center text-primary-dark">
                  <TableCell className="min-w-min py-5 text-center">
                    <a className="no-underline" href={url} target="__blank">
                      {url}
                    </a>
                  </TableCell>
                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {dayjs(presignedUrl.createdAt).format('DD.MM.YYYY')}
                  </TableCell>
                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {dayjs(presignedUrl.expiryDate).format('DD.MM.YYYY')}
                  </TableCell>
                  <TableCell className="max-w-40 whitespace-normal break-words py-5 text-center lg:w-1/4">
                    {presignedUrl.additionalData?.editingMode === 'view'
                      ? t('view-only')
                      : presignedUrl.additionalData?.editingMode === 'limited'
                        ? t('limited')
                        : '----'}
                  </TableCell>
                  <TableCell>
                    <div className="hidden w-auto min-w-48 items-center justify-end gap-1 py-5 text-center lg:grid lg:grid-cols-2 xl:flex">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(url);
                          toast.success(t('copied-url'));
                        }}
                        variant="outline"
                        className="border-2"
                      >
                        <Copy aria-hidden />
                        <span className="sr-only">{t('copy')}</span>
                      </Button>
                      <ShareDigiTwinPopup
                        open={
                          settingPopup?.type === 'EDIT' && settingPopup.token === presignedUrl.token
                        }
                        onOpen={() => openShareLinkSettingPopup(presignedUrl.token)}
                        onShare={(values) => updatePresignedUrl(presignedUrl.token, values)}
                        onCancel={closeShareLinkSettingPopup}
                        defaultValues={{
                          ...presignedUrl.additionalData,
                          expiresIn: String(expiresIn),
                          editingMode: presignedUrl.additionalData?.editingMode || 'view',
                        }}
                      >
                        <Button variant="outline" className="border-2">
                          <SettingsIcon aria-hidden />
                          <span className="sr-only">{t('settings')}</span>
                        </Button>
                      </ShareDigiTwinPopup>
                      <Button
                        className="max-xl:max-w-[60px]"
                        onClick={() => handleRevoke(presignedUrl.token)}
                        variant="destructive"
                      >
                        <TrashIcon aria-hidden />
                        <span className="sr-only"> {t('delete')}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="flex justify-center py-16">
          <span>{t('noDocuments')}</span>
        </div>
      )}
    </ModalBase>
  );
};
